import type { SelectedFilters } from 'src/app/portfolio/state/portfolioGeneralSlice';
import type { SiteFilterOptions } from 'src/app/portfolio/state/api/siteFilterOptionsQuery';

const KBA_LEVELS = ['within_boundary', 'within_buffer', 'no_overlap'] as const;
const RISK_LEVELS = ['VERY_HIGH', 'HIGH', 'MEDIUM', 'LOW', 'VERY_LOW'] as const;
const STATE_OF_NATURE_LEVELS = ['VERY_POOR', 'POOR', 'MODERATE', 'GOOD', 'VERY_GOOD'] as const;

export const SITE_FILTER_NAMES = [
    'country',
    'isic',
    'kba',
    'physical_risk_levels',
    'dependency_levels',
    'impact_levels',
    'son_levels',
] as const;

export type SiteFilterName = typeof SITE_FILTER_NAMES[number];

export const isSiteFilterName = (name: string): name is SiteFilterName => (SITE_FILTER_NAMES as readonly string[]).includes(name);

export const sanitizeSelectedFilters = (filters: SelectedFilters, filterOptions?: SiteFilterOptions): SelectedFilters => {
    // Typed against SITE_FILTER_NAMES, so a new name without an entry here will not compile.
    const allowedByFilter: Record<SiteFilterName, Set<string>> = {
        country: new Set(filterOptions?.countries.map(country => country.code) ?? []),
        isic: new Set(filterOptions?.isicCodes.map(isicCode => isicCode.code) ?? []),
        kba: new Set(KBA_LEVELS),
        physical_risk_levels: new Set(RISK_LEVELS),
        dependency_levels: new Set(RISK_LEVELS),
        impact_levels: new Set(RISK_LEVELS),
        son_levels: new Set(STATE_OF_NATURE_LEVELS),
    };

    return filters.reduce<SelectedFilters>((acc, filter) => {
        if (!isSiteFilterName(filter.name)) {
            return acc;
        }
        const allowed = allowedByFilter[filter.name];

        const values = [...new Set(filter.values.filter(value => allowed.has(value)))];
        if (values.length > 0) {
            acc.push({ name: filter.name, values });
        }
        return acc;
    }, []);
};

export const areSelectedFiltersEqual = (left: SelectedFilters, right: SelectedFilters): boolean => {
    if (left.length !== right.length) return false;
    return left.every((leftFilter, index) => {
        const rightFilter = right[index];
        if (!rightFilter || leftFilter.name !== rightFilter.name || leftFilter.values.length !== rightFilter.values.length) {
            return false;
        }
        return leftFilter.values.every((value, valueIndex) => value === rightFilter.values[valueIndex]);
    });
};
